import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getWorkflowDefinitions,
  deleteWorkflowDefinition,
  activateWorkflowDefinition,
  deactivateWorkflowDefinition,
  type WorkflowDefinition,
  type WorkflowDefinitionFilters,
} from '@/api/workflowApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  active: 'green',
  inactive: 'orange',
};

const TRIGGER_COLORS: Record<string, string> = {
  manual: 'blue',
  event: 'purple',
  scheduled: 'cyan',
};

export default function WorkflowDefinitionListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<WorkflowDefinitionFilters>({});

  const canWrite = hasPermission('workflows', 'write');
  const canDelete = hasPermission('workflows', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof WorkflowDefinitionFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getWorkflowDefinitions({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('workflows.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteWorkflowDefinition(id);
            message.success(t('workflows.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleActivate = useCallback(
    async (id: string) => {
      try {
        await activateWorkflowDefinition(id);
        message.success(t('workflows.activate_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const handleDeactivate = useCallback(
    async (id: string) => {
      try {
        await deactivateWorkflowDefinition(id);
        message.success(t('workflows.deactivate_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<WorkflowDefinition> = useMemo(
    () => [
      {
        title: t('workflows.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        ellipsis: true,
        render: (name: string, record: WorkflowDefinition) => (
          <a onClick={() => navigate(`/workflows/definitions/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('workflows.field.trigger_type'),
        dataIndex: 'trigger_type',
        key: 'trigger_type',
        width: 120,
        render: (type: string) => (
          <Tag color={TRIGGER_COLORS[type] ?? 'default'}>
            {t(`workflows.trigger_type_options.${type}`, type)}
          </Tag>
        ),
      },
      {
        title: t('workflows.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`workflows.status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('workflows.field.version'),
        dataIndex: 'version',
        key: 'version',
        width: 80,
        align: 'center',
        render: (version: number) => `v${version}`,
      },
      {
        title: t('workflows.field.steps'),
        dataIndex: 'steps',
        key: 'steps',
        width: 80,
        align: 'center',
        render: (steps: unknown[]) => steps?.length ?? 0,
      },
      {
        title: t('workflows.field.tags'),
        dataIndex: 'tags',
        key: 'tags',
        width: 160,
        ellipsis: true,
        render: (tags: string[]) =>
          tags?.length > 0
            ? tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-',
      },
      {
        title: t('workflows.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 170,
        sorter: true,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 180,
        render: (_: unknown, record: WorkflowDefinition) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/workflows/definitions/${record.id}`)}
            />
            {canWrite && (
              <>
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/workflows/definitions/${record.id}/edit`)}
                />
                {record.status !== 'active' && (
                  <Button
                    type="link"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={() => handleActivate(record.id)}
                    title={t('workflows.action.activate')}
                  />
                )}
                {record.status === 'active' && (
                  <Button
                    type="link"
                    size="small"
                    icon={<PauseCircleOutlined />}
                    onClick={() => handleDeactivate(record.id)}
                    title={t('workflows.action.deactivate')}
                  />
                )}
              </>
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handleActivate, handleDeactivate]
  );

  const triggerTypeOptions = useMemo(
    () => [
      { value: '', label: t('workflows.filter.all') },
      { value: 'manual', label: t('workflows.trigger_type_options.manual') },
      { value: 'event', label: t('workflows.trigger_type_options.event') },
      { value: 'scheduled', label: t('workflows.trigger_type_options.scheduled') },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('workflows.filter.all') },
      { value: 'draft', label: t('workflows.status_options.draft') },
      { value: 'active', label: t('workflows.status_options.active') },
      { value: 'inactive', label: t('workflows.status_options.inactive') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('workflows.field.trigger_type')}
        style={{ width: 140 }}
        allowClear
        options={triggerTypeOptions}
        onChange={(val) => handleFilterChange('trigger_type', val || undefined)}
      />
      <Select
        placeholder={t('workflows.field.status')}
        style={{ width: 120 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/workflows/definitions/new')}
        >
          {t('workflows.new_workflow')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('workflows.definitions_title')}</h2>
      <DataGrid<WorkflowDefinition>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('workflows.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/workflows/definitions/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
